import { MeshBuilder, StandardMaterial, type Mesh } from "@babylonjs/core";
import { HeroId, Slot, fxk, hv, pk } from "@rivalwatch/shared";
import * as loops from "../../audio/cues/loops.ts";
import type { Out } from "../../audio/engine.ts";
import { brass, click, fm, noise, thump, tone, whoosh } from "../../audio/synth.ts";
import type { ClientHero, FxCtx, ProjectileFrame } from "../types.ts";

const V = { leap: 0, monsterTimer: 1 } as const;
const PK = { Gamma: pk(HeroId.Bulk, 0), Lockdown: pk(HeroId.Bulk, 1), Grenade: pk(HeroId.Bulk, 2) } as const;
const FX = { Gamma: fxk(HeroId.Bulk, 0), Slam: fxk(HeroId.Bulk, 1), Transform: fxk(HeroId.Bulk, 2) } as const;

const gammaCue = (o: Out) => {
  fm(o, { f: 120, ratio: 2.1, index: 5, gain: 0.55, d: 0.3 });
  noise(o, { color: "pink", type: "bandpass", f: 900, gain: 0.92, d: 0.18 });
  click(o, 0.35, 2600, 0.004);
  return 0.55;
};
const punchCue = (o: Out) => {
  thump(o, 0.8, 64, 30, 0.22);
  whoosh(o, 0.36, 260, 1200, 0.2);
  return 0.4;
};
const transformCue = (o: Out) => {
  tone(o, { type: "sawtooth", f: 65, f1: 180, glide: 0.5, gain: 0.095, d: 0.8, lp: 700 });
  noise(o, { color: "brown", type: "lowpass", f: 500, gain: 0.38, d: 0.7 });
  thump(o, 0.78, 52, 22, 0.5);
  return 1.2;
};
const theme = {
  bpm: 82,
  octave: -1,
  voice: brass,
  notes: [
    [0, 0.5],
    [null, 0.25],
    [4, 0.25],
    [5, 0.5],
    [0, 0.5],
    [-5, 1],
  ],
} satisfies ClientHero["audio"]["theme"];

const hero: ClientHero<{
  ray: Mesh;
  nozzle: Mesh;
  fists: Mesh[];
  knuckles: Mesh[];
  back: Mesh;
  glasses: Mesh;
  brow: Mesh;
  collar: Mesh;
}> = {
  id: HeroId.Bulk,
  bulk: 1.05,
  shape: { head: 1, torso: 1.04, arms: 1.06, legs: 1 },
  forms: [
    { bulk: 1.28, shape: { head: 1.08, torso: 1.2, arms: 1.32, legs: 1.1 } },
    { bulk: 1.55, shape: { head: 1.15, torso: 1.3, arms: 1.55, legs: 1.16 } },
  ],
  gear(g) {
    const { scene, rig, add, toon, glow } = g;
    const ray = add(MeshBuilder.CreateBox("bulk-ray-gun", { width: 0.16, height: 0.18, depth: 0.64 }, scene), rig.handR, toon("#344050"));
    ray.position.z = -0.38;
    const nozzle = add(MeshBuilder.CreateCylinder("bulk-ray-nozzle", { diameter: 0.14, height: 0.12, tessellation: 10 }, scene), rig.handR, glow("#b8ff8f", 0.8));
    nozzle.rotation.x = Math.PI / 2;
    nozzle.position.z = -0.72;
    const fists = [rig.handL, rig.handR].map((hand, i) => {
      const fist = add(MeshBuilder.CreateBox(`bulk-fist-${i}`, { width: 0.32, height: 0.28, depth: 0.3 }, scene), hand, toon("#57b346"));
      fist.position.z = -0.12;
      return fist;
    });
    const knuckles = [rig.handL, rig.handR].map((hand, i) => {
      const k = add(MeshBuilder.CreateBox(`bulk-knuckles-${i}`, { width: 0.28, height: 0.05, depth: 0.08 }, scene), hand, glow("#b8ff8f", 0.45));
      k.position.set(0, 0.06, -0.28);
      return k;
    });
    const back = add(MeshBuilder.CreateBox("bulk-back-rig", { width: 0.55, height: 0.22, depth: 0.16 }, scene), rig.torso, glow("#b8ff8f", 0.45));
    back.position.set(0, 0.1, 0.42);
    const glasses = add(MeshBuilder.CreateBox("banter-glasses", { width: 0.42, height: 0.07, depth: 0.04 }, scene), rig.head, toon("#18212b"));
    glasses.position.set(0, 0.04, -0.29);
    const brow = add(MeshBuilder.CreateBox("bulk-brow", { width: 0.46, height: 0.1, depth: 0.08 }, scene), rig.head, toon("#183b17"));
    brow.position.set(0, 0.12, -0.3);
    const collar = add(MeshBuilder.CreateBox("banter-lab-collar", { width: 0.62, height: 0.08, depth: 0.12 }, scene), rig.torso, toon("#efffde"));
    collar.position.set(0, 0.34, -0.28);
    return { ray, nozzle, fists, knuckles, back, glasses, brow, collar };
  },
  animate(gear, f) {
    const form = f.form;
    const monster = form === 2;
    gear.ray.setEnabled(form === 0);
    gear.nozzle.setEnabled(form === 0);
    gear.glasses.setEnabled(form === 0);
    gear.collar.setEnabled(form === 0);
    gear.brow.setEnabled(form > 0);
    for (const fist of gear.fists) fist.setEnabled(form > 0);
    for (const knuckle of gear.knuckles) {
      knuckle.setEnabled(form > 0);
      knuckle.scaling.setAll(monster ? 1.35 : 1);
    }
    gear.back.setEnabled(form > 0);
    gear.back.scaling.setAll(monster ? 1.8 : 1.2);
    if (form === 0) return f.action === 1 ? "holding-right-shoot" : "holding-right";
    if (f.moveKind === 5 || f.v[V.leap] > 0) return "attack-kick-right";
    if (f.action === 1 || f.action === 3) return "attack-melee-right";
    if (f.action === 2 || f.action === 4) return "holding-both-shoot";
    return "holding-both";
  },
  hud(c, eid) {
    const form = c.Player.form[eid]!;
    return [
      { label: form === 0 ? "Banter" : form === 1 ? "Bulk" : "Monster", value: form === 2 ? hv(c, eid, V.monsterTimer) / 720 : 1, color: form === 0 ? "#d7a57d" : "#77ff55" },
    ];
  },
  projectiles: {
    [PK.Gamma](scene, f: ProjectileFrame) {
      const mat = new StandardMaterial("bulk-gamma-mat", scene);
      mat.emissiveColor.set(0.35, 1, 0.25);
      const mesh = MeshBuilder.CreateSphere("bulk-gamma", { diameter: f.kind === PK.Lockdown ? 0.35 : 0.55, segments: 12 }, scene);
      mesh.material = mat;
      return {
        gear: mesh,
        update(m: typeof mesh, frame) {
          m.position.set(frame.pos.x, frame.pos.y, frame.pos.z);
          m.scaling.setAll(1 + Math.sin(frame.age * 20) * 0.08);
        },
        dispose() {
          mesh.dispose();
          mat.dispose();
        },
      };
    },
    [PK.Lockdown](scene, f) {
      return hero.projectiles![PK.Gamma]!(scene, f);
    },
    [PK.Grenade](scene, f) {
      return hero.projectiles![PK.Gamma]!(scene, f);
    },
  },
  fx: {
    [FX.Slam]: {
      explosion(c: FxCtx, pos, radius) {
        const ring = MeshBuilder.CreateTorus("bulk-slam-ring", { diameter: radius * 2, thickness: 0.12, tessellation: 48 }, c.scene);
        ring.position.set(pos.x, pos.y + 0.06, pos.z);
        ring.material = c.glow("#77ff55", 0.65);
        c.timed(ring, 0.5, (t, m) => m.scaling.setAll(0.25 + t));
      },
      keepGeneric: true,
    },
    [FX.Transform]: {
      explosion(c: FxCtx, pos, radius) {
        const ring = MeshBuilder.CreateTorus("bulk-transform-pop", { diameter: radius * 1.6, thickness: 0.08, tessellation: 48 }, c.scene);
        ring.position.set(pos.x, pos.y + 0.08, pos.z);
        ring.material = c.glow("#b8ff8f", 0.55);
        c.timed(ring, 0.45, (t, m) => m.scaling.setAll(0.25 + t));
      },
    },
  },
  audio: {
    weight: "heavy",
    fire: (ctx) => ({ key: ctx?.form === 0 ? "fire:bulk:ray" : ctx?.alt ? "fire:bulk:gamma" : "fire:bulk:punch", cue: ctx?.form === 0 ? gammaCue : ctx?.alt ? gammaCue : punchCue }),
    ability: {
      [Slot.Secondary]: gammaCue,
      [Slot.Ability1]: transformCue,
      [Slot.Ability2]: gammaCue,
      [Slot.Ultimate]: transformCue,
    },
    abilityForms: {
      1: { [Slot.Secondary]: gammaCue, [Slot.Ability2]: gammaCue, [Slot.Ability1]: transformCue, [Slot.Ultimate]: transformCue },
      2: { [Slot.Secondary]: gammaCue, [Slot.Ability2]: gammaCue, [Slot.Ability1]: transformCue, [Slot.Ultimate]: punchCue },
    },
    formChange: { 1: transformCue, 2: transformCue },
    theme,
    themeTrim: { heroic: -1, hostile: -7, tease: -2 },
    loops: ({ form, alive }) => (alive && form === 2 ? [{ key: "monster", build: loops.motor, gain: 0.5 }] : []),
    flight: { [PK.Gamma]: loops.flight("orb"), [PK.Lockdown]: loops.flight("orb"), [PK.Grenade]: loops.flight("orb") },
    impacts: { [FX.Gamma]: gammaCue },
    explosions: { [FX.Slam]: () => punchCue, [FX.Transform]: () => transformCue },
  },
  icons: {
    [Slot.Primary]: "M5 15c3-4 11-4 14 0M8 12l-2-2 2-2M16 12l2-2-2-2",
    [Slot.Secondary]: "M4 12c4-6 12-6 16 0-4 6-12 6-16 0z",
    [Slot.Ability1]: "M12 4l8 5v6l-8 5-8-5V9zM12 8v8",
    [Slot.Ability2]: "M6 7h12v10H6zM9 10h6M9 14h6",
    [Slot.Ultimate]: "M7 20l5-16 5 16M5 12h14",
    passive: "M6 18c1-5 11-5 12 0M8 8l4-4 4 4M9 13h6",
  },
};

export default hero;
